import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { partBomService } from 'partBoms/services';
import { IGetPartBomsPayload, IPartBom, IUpsertPartBomPayload } from 'partBoms/types';

export const useGetPartBoms = (
  requestPayload?: IGetPartBomsPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) => useInfiniteQuery<IPartBom>(partBomService.getPartBoms, 'partBoms', requestPayload, options);

export const useGetPartBom = (
  criteria: { part: { id: number } },
  options: { enabled?: boolean } = { enabled: true }
) => {
  const { data, isLoading, isFetching, isError } = useQuery(
    ['partBomDetails', criteria],
    ({ signal }) => partBomService.getPartBom(criteria, signal),
    {
      keepPreviousData: false,
      enabled: options.enabled,
    }
  );

  return {
    record: data?.record ?? null,
    isLoading,
    isFetching,
    isError,
  };
};

export const usePartBomActions = () => ({
  ...useUpsertPartBom(),
  ...useRemovePartBom(),
});

const useUpsertPartBom = () => {
  const { errors } = useHandleErrors({
    success: ({ part }) =>
      part.id
        ? { message: t('messagePartBomUpdateSuccess') }
        : { message: t('messagePartBomCreateSuccess') },
    loading: ({ part }) =>
      part.id
        ? { message: t('messagePartBomUpdateInProgress') }
        : { message: t('messagePartBomCreateInProgress') },
    error: ({ part }) =>
      part.id ? { title: t('errorTitlePartBomUpdate') } : { title: t('errorTitlePartBomCreate') },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['partBoms', 'partBomDetails'],
  });

  const { mutateAsync } = useMutation(
    (data: IUpsertPartBomPayload) => partBomService.upsertPartBom(data),
    {
      ...errors,
    }
  );

  return { upsertPartBom: mutateAsync };
};

const useRemovePartBom = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messagePartBomRemoveSuccess') },
    loading: { message: t('messagePartBomRemoveInProgress') },
    error: { title: t('errorTitlePartBomRemove') },
    position: NotificationPosition.TOP,
    invalidateQueries: ['partBoms'],
  });

  const { mutateAsync } = useMutation(
    (data: { ids: number[] }) => partBomService.removePartBom(data),
    {
      ...errors,
    }
  );

  return { removePartBom: mutateAsync };
};
